'use client'

import {
  PremiumButton,
  PremiumFeature,
  PremiumHeading,
  PremiumHero,
  PremiumImageCard,
  PremiumIntro,
  PremiumSection,
} from '@/components/premium/premium-ui'
import { ArrowRight, Handshake, Hotel, Landmark, Leaf, Map, BriefcaseBusiness, Users } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'

const collaborations = [
  {
    title: 'Sirumugai Story',
    label: 'StartupTN',
    image: '/sirumugai-story.jpeg',
    body: 'Bringing travellers into a traditional weaving village.',
  },
  {
    title: 'Experience Calendar 2026',
    label: 'TIDCO',
    image: '/experiences/kanchivaram-silk-weaving.jpg',
    body: 'A year-round view of cultural experiences across Tamil Nadu.',
  },
  {
    title: 'Heritage Trails',
    label: 'Tourism',
    image: '/experiences/paddy-harvesting-in-wet-fields.jpg',
    body: 'Local routes shaped with hosts and cultural custodians.',
  },
]

export const CollaborationsPage = () => {
  return (
    <div className="bg-background">
      <PremiumHero
        image="/experiences/pongalo-pongal-with-family.jpg"
        imageAlt="Sharing Pongal meal with a Tamil family — Oorva partnerships with local communities"
        eyebrow="Partnerships"
        title={
          <>
            Local culture.
            <br />
            <span className="italic text-accent-foreground">Shared impact.</span>
          </>
        }
        subtitle="Partner with Oorva to create thoughtful experiences with local communities."
      >
        <PremiumButton href="mailto:hello@oorva.in?subject=Oorva%20partnership%20enquiry">Discuss a partnership</PremiumButton>
      </PremiumHero>

      <PremiumSection className="!py-14 md:!py-20">
        <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand/20 bg-accent px-4 py-2 text-xs font-bold uppercase tracking-widest text-accent-foreground">
                <Handshake className="h-4 w-4" />
                Official partnership
              </div>
              <PremiumHeading size="compact">
                Working with Tamil Nadu
              </PremiumHeading>
              <PremiumIntro className="mt-5">
                Together with the Department of Tourism and Guidance Tamil Nadu, we support
                community-led experiences that are thoughtful, safe and locally rooted.
              </PremiumIntro>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                ['/dot-logo.png', 'Department of Tourism'],
                ['/oorva-new-logo.png', 'Oorva'],
                ['/gui-logo-white.png', 'Guidance Tamil Nadu'],
              ].map(([src, alt]) => (
                <div key={alt} className="grid min-h-36 place-items-center rounded-2xl border border-white/10 bg-primary p-6">
                  <div className="relative h-16 w-full">
                    <Image src={src} alt={alt} fill className="object-contain" sizes="(min-width: 640px) 18vw, 60vw" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection className="!py-14 md:!py-20">
        <div className="mb-10">
          <PremiumHeading size="compact">Selected work</PremiumHeading>
          <PremiumIntro className="mt-3">Examples of the kinds of programmes we shape with partners. Ask us about the scope and outcomes relevant to your organisation.</PremiumIntro>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {collaborations.map((item) => (
            <PremiumImageCard key={item.title} {...item} />
          ))}
        </div>
      </PremiumSection>

      <PremiumSection tone="forest" className="!py-14 md:!py-20">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <PremiumHeading size="compact" className="text-white">What we build</PremiumHeading>
            <p className="mt-5 max-w-md text-white/68">
              Experiences that respect local knowledge and create lasting value.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <PremiumFeature dark icon={<Map className="h-5 w-5" />} title="Experience calendars" body="Seasonal programmes for places and communities." />
            <PremiumFeature dark icon={<Users className="h-5 w-5" />} title="Community onboarding" body="Finding, training and supporting local hosts." />
            <PremiumFeature dark icon={<Landmark className="h-5 w-5" />} title="Heritage trails" body="Routes built around craft, food and festivals." />
            <PremiumFeature dark icon={<Leaf className="h-5 w-5" />} title="Impact reporting" body="Useful measures of cultural and rural impact." />
          </div>
        </div>
      </PremiumSection>

      <PremiumSection className="!py-14 md:!py-20">
        <div className="mb-10">
          <PremiumHeading size="compact">Solutions for every partner</PremiumHeading>
          <PremiumIntro className="mt-3">Pick the track that matches who you are. We shape the rest with you.</PremiumIntro>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-7">
            <span className="grid size-11 place-items-center rounded-full bg-brand-soft text-brand">
              <Hotel className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-sans text-2xl font-semibold tracking-[-0.02em]">Hotels & resorts</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Give every stay a distinctive sense of place with verified local experiences your guests can book in minutes.
            </p>
            <ul className="mt-4 space-y-2 text-sm leading-6 text-muted-foreground">
              <li>· Curated experience menu for your property</li>
              <li>· On-ground coordination with hosts</li>
              <li>· Priority slots for your guests</li>
            </ul>
          </div>
          <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-7">
            <span className="grid size-11 place-items-center rounded-full bg-brand-soft text-brand">
              <Landmark className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-sans text-2xl font-semibold tracking-[-0.02em]">Governments & institutions</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Build responsible tourism around living culture — host discovery, programme design and local participation.
            </p>
            <ul className="mt-4 space-y-2 text-sm leading-6 text-muted-foreground">
              <li>· Experience calendars & heritage trails</li>
              <li>· Community onboarding & training</li>
              <li>· Impact reporting for stakeholders</li>
            </ul>
          </div>
          <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-7">
            <span className="grid size-11 place-items-center rounded-full bg-brand-soft text-brand">
              <BriefcaseBusiness className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-sans text-2xl font-semibold tracking-[-0.02em]">Travel operators</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Add authentic, destination-specific moments to itineraries with curated inventory and reliable coordination.
            </p>
            <ul className="mt-4 space-y-2 text-sm leading-6 text-muted-foreground">
              <li>· Itinerary-ready experience slots</li>
              <li>· Group & private session options</li>
              <li>· Single point of contact on-ground</li>
            </ul>
            <Link
              href="/register-interest"
              className="mt-5 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand transition hover:text-brand-hover"
            >
              Register as a tour operator partner <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection className="!py-14 md:!py-20">
        <div className="flex flex-col items-start justify-between gap-6 rounded-[1.5rem] border border-border bg-card p-6 md:flex-row md:items-center md:p-8">
          <div>
            <PremiumHeading size="compact">Build something meaningful</PremiumHeading>
            <PremiumIntro className="mt-3">Tell us your organisation, destination, group size and what you hope to build. We will confirm a contact person, scope and next steps after your enquiry.</PremiumIntro>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <PremiumButton href="mailto:hello@oorva.in?subject=Oorva%20partnership%20enquiry">Email your proposal</PremiumButton>
            <PremiumButton href="https://wa.me/918754035228?text=Hi%20Oorva!%20I%20want%20to%20partner%20on%20local%20experiences.">Chat on WhatsApp</PremiumButton>
          </div>
        </div>
      </PremiumSection>
    </div>
  )
}
